import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { FilterParam } from 'common/src/query-params/FilterParam';
import { SingleOperators } from 'common/src/query-params/operators';

import { getAlertCount } from 'portal/src/api/AlertsService';
import { QUERY_KEYS } from 'portal/src/api/constants';
import useAuth from 'portal/src/auth/useAuth';
import { useHasAlertsFeature } from 'portal/src/hooks/useHasAlertsFeature';
import { getEnabledAlertSubtypesAndFilter } from 'portal/src/pages/alerts/utils/alertsFiltering';

export function useAlertsBadgeCount() {
  const { getUser } = useAuth();
  const user = getUser();
  const preferences = user?.alertPreferences;
  const alertsViewedAt = user?.alertsViewedAt;
  const hasAlertsFeature = useHasAlertsFeature();

  const { enabledSubtypes, subtypeFilter: subTypeFilter } = useMemo(
    () => getEnabledAlertSubtypesAndFilter(preferences),
    [preferences],
  );
  const hasEnabledFilters = enabledSubtypes.length > 0;

  const badgeFilters = useMemo(() => {
    const filters: FilterParam[] = [subTypeFilter];

    if (alertsViewedAt) {
      filters.push({
        field: 'createdAt',
        operator: SingleOperators.GREATER_THAN,
        value: alertsViewedAt,
      });
    }

    return filters;
  }, [alertsViewedAt, subTypeFilter]);

  const { data: alertsCountData, isError } = useQuery({
    queryKey: [QUERY_KEYS.alertsBadgeCount, enabledSubtypes, alertsViewedAt],
    queryFn: () => getAlertCount({ filters: badgeFilters }),
    enabled: hasAlertsFeature && !!user && hasEnabledFilters,
    refetchOnMount: 'always',
    refetchInterval: 60000, // Refetch every minute
    refetchIntervalInBackground: true,
    initialData: { count: 0 },
  });

  return {
    count: hasEnabledFilters ? alertsCountData.count : 0,
    isError,
  };
}
